/* eslint-disable react/destructuring-assignment */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import Grid from "../../../../../../components/Habitacional/core/Grid";
import Button from "../../../../../../components/Habitacional/core/Button";
import ContentSection from "../../../../../../components/Habitacional/ContentSection";
import {
  CheckListItemsDeathByAccident,
  CheckListItemsDeathByDisease,
  CheckListItemsIncapacityByAccident,
  CheckListItemsIncapacityByDisease,
} from "../utils/ConstantsFormMIP";
import { handleScrollSmooth } from "../../../../../../components/Habitacional/CardActivateInsurance/CardActivateInsurance";
import { ReactComponent as DocumentIcon } from "../../../../../../assets/images/habitacional/illustration-document-gael.svg";
import { ReactComponent as SuccessIcon } from "../../../../../../assets/images/habitacional/success-circle.svg";

import useWindowSize from "../../../../../../hooks/useWindowSize";
import useAuthToken from "../../../../../../hooks/requests/useAuthToken";

import { envs } from "../../../../../../config";

import * as S from "./FormStepTwoMIP.Style";
import CSLoading from "../../../../../../components/common/CSLoading";
import {
  ConvertBase64ToBlob,
  DocumentCauseForId,
  getDocumentListByCause,
  getMacDocumentId,
  ValidateColor,
} from "../../utils/FormUtils";

const Divider = (isMobile) => isMobile && <S.Divider />;

const FormStepTwoMIP = ({
  title = "Para analisarmos o seu pedido, precisamos que envie alguns documentos.",
  description,
  onUpdateSendDocuments,
  comunicatorId,
  nextStep,
  confirmCause,
}) => {
  const { watch } = useFormContext();
  const { width } = useWindowSize();
  const { refreshToken } = useAuthToken();

  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { handleSubmit, getValues } = methods;

  const [isLoading, setIsLoading] = useState(false);
  const [sendDocuments, setSendDocuments] = useState(false);
  const [documentState, setDocumentState] = useState({
    documentListDeathByAccident: CheckListItemsDeathByAccident,
    documentListDeathByDisease: CheckListItemsDeathByDisease,
    documentListIncapacityByAccident: CheckListItemsIncapacityByAccident,
    documentListIncapacityByDisease: CheckListItemsIncapacityByDisease,
  });

  const isMobile = width <= 768;
  const ConfirmCause = confirmCause || watch("SelectCauseIncident");
  const documentList = getDocumentListByCause(ConfirmCause?.id, documentState);

  const handleScrollSendDocuments = () => {
    setTimeout(() => {
      const element = document.getElementsByName("envioDeDocumentos");
      const offsetTop =
        element[0]?.getBoundingClientRect()?.top + window.scrollY - 100;
      window.scrollTo({ top: offsetTop, behavior: "smooth" });
    }, 100);
  };

  async function onSubmit() {
    setIsLoading(true);
    const data = getValues();
    const token = await refreshToken();
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      if (data[key]) {
        const blob = ConvertBase64ToBlob(data[key].base64, data[key].type);

        formData.append(
          "documento",
          JSON.stringify({
            numeroComunicado: comunicatorId,
            idTipoDocumento: getMacDocumentId(key),
            nomeAnexo: data[key].name,
            mineType: data[key].type,
          })
        );

        formData.append("file", blob, data[key].name);
      }
      return data[key];
    });

    const myHeaders = new Headers();
    myHeaders.append("X-IBM-Client-Id", envs.clientId);
    myHeaders.append("scope", "clientes");
    myHeaders.append("Cache-Control", "no-cache");
    myHeaders.append("Authorization", `Bearer ${token}`);

    try {
      const url = `${envs.hostApic}/segurohabitacional/v1/operaoes-documentos/documentos`;
      const response = await fetch(url, {
        method: "POST",
        headers: myHeaders,
        body: formData,
      });

      await response.json();

      if (nextStep) {
        nextStep();
      }
    } catch {
      alert("erro ao enviar documentos");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const allDocumentsSent = documentList?.every((doc) => doc.send === true);

    if (onUpdateSendDocuments) {
      onUpdateSendDocuments(allDocumentsSent);
    }
  }, [documentList, onUpdateSendDocuments]);

  if (isLoading) {
    return <CSLoading center />;
  }

  return (
    <>
      <ContentSection
        illustration={<DocumentIcon />}
        title={title}
        description={
          description ?? (
            <>
              Confira os documentos que você precisa enviar para{" "}
              <strong>{ConfirmCause?.label}</strong>. Você pode fazer isso agora
              ou depois.
            </>
          )
        }
        noPadding
      >
        <S.ContentCheckListDocuments>
          {documentList?.map(({ id, label, send }) => (
            <S.CheckListDocuments key={id}>
              <SuccessIcon style={{ color: ValidateColor(send) }} />
              {label}
            </S.CheckListDocuments>
          ))}
        </S.ContentCheckListDocuments>

        <S.ContentButtonsDocument>
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              setSendDocuments(true);
              handleScrollSendDocuments();
            }}
          >
            Enviar documentos
          </Button>

          {nextStep && (
            <Button
              variant="muted-primary"
              type="button"
              onClick={() => {
                handleScrollSmooth();
                nextStep();
              }}
            >
              Continuar sem enviar
            </Button>
          )}
        </S.ContentButtonsDocument>
      </ContentSection>

      {sendDocuments && (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <S.Divider />
            <S.ContainerAttachmentAndSendDocuments>
              <S.Title name="envioDeDocumentos">Envio de documentos</S.Title>
              <S.ContentAttachmentAndSendDocuments>
                <Grid container spacingColumn={4} spacingRow={3}>
                  <Grid item md={12}>
                    <S.Description>
                      Anexe uma foto ou cópia digitalizada de cada documento. Se
                      houver mais páginas, salve em um único arquivo nos
                      formatos JPG, PDF ou PNG. Certifique-se de que as
                      informações estejam legíveis.
                    </S.Description>
                  </Grid>
                  <Grid item md={12}>
                    <S.Description>
                      Se não tiver todos os documentos, continue o pedido e
                      anexe-os depois. A análise começa após o envio de todos os
                      documentos necessários.
                    </S.Description>
                  </Grid>
                  {DocumentCauseForId(
                    ConfirmCause?.id,
                    documentState,
                    setDocumentState
                  )}
                </Grid>
              </S.ContentAttachmentAndSendDocuments>
            </S.ContainerAttachmentAndSendDocuments>

            {Divider(isMobile)}

            {nextStep ? (
              <S.ContentButtonsNextAndBack>
                <Button
                  variant="muted-primary"
                  type="button"
                  onClick={() => {
                    setSendDocuments(false);
                    handleScrollSmooth();
                  }}
                >
                  Voltar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={() => {
                    handleScrollSmooth();
                  }}
                  disabled={isLoading}
                >
                  Continuar
                </Button>
              </S.ContentButtonsNextAndBack>
            ) : (
              <S.ContentButtonsNextAndBack>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={() => {
                    handleScrollSmooth();
                  }}
                  disabled={isLoading}
                >
                  Enviar
                </Button>
              </S.ContentButtonsNextAndBack>
            )}
          </form>
        </FormProvider>
      )}
    </>
  );
};

FormStepTwoMIP.propTypes = {
  title: PropTypes.string,
  description: PropTypes.element,
  onUpdateSendDocuments: PropTypes.func,
  comunicatorId: PropTypes.string,
  nextStep: PropTypes.func,
  confirmCause: PropTypes.objectOf(Object),
};

FormStepTwoMIP.defaultProps = {
  title:
    "Para analisarmos o seu pedido, precisamos que envie alguns documentos.",
  description: null,
  onUpdateSendDocuments: null,
  comunicatorId: null,
  nextStep: null,
  confirmCause: null,
};

export default FormStepTwoMIP;
